var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import * as React from "react";
import { Icon } from "@/components/ui/Icon";
import { cn } from "@/lib/utils";
const SVG_FILL = "[&_svg]:fill-current [&_svg_*]:fill-current";
/**
 * Bloom 2.0 actions: height 40, pill radius. Fill and border never mix —
 * secondary is filled without a border, outline is a border without a fill.
 * Icon-only buttons use `size="icon"` (32×32, radius nav).
 */
const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-pill text-body font-semibold leading-none transition-colors focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:w-4 [&_svg]:h-4", {
    variants: {
        size: {
            sm: "h-control-sm px-3.5 text-small",
            md: "h-control px-4",
            lg: "h-12 px-6",
            icon: "h-control-sm w-control-sm rounded-nav p-0",
        },
        variant: {
            // Secondary: filled, no border
            "button-base-alt": `bg-btn-secondary text-ink hover:bg-btn-secondary-hover active:bg-btn-secondary-active disabled:text-ink-muted ${SVG_FILL}`,
            "base-alt": `bg-btn-secondary text-ink hover:bg-btn-secondary-hover active:bg-btn-secondary-active disabled:text-ink-muted ${SVG_FILL}`,
            secondary: "bg-btn-secondary text-ink hover:bg-btn-secondary-hover active:bg-btn-secondary-active disabled:text-ink-muted",
            // Outline: border, no fill
            base: `border border-control bg-transparent font-medium text-ink hover:border-control-hover hover:bg-ghost-hover active:bg-ghost-active disabled:border-line disabled:text-ink-muted ${SVG_FILL}`,
            outline: "border border-control bg-transparent font-medium text-ink hover:border-control-hover hover:bg-ghost-hover active:bg-ghost-active disabled:border-line disabled:text-ink-muted",
            // Ghost: no fill until hovered
            "base-no-border": `bg-transparent font-medium text-ink hover:bg-ghost-hover active:bg-ghost-active disabled:text-ink-muted ${SVG_FILL}`,
            ghost: "bg-transparent font-medium text-ink hover:bg-ghost-hover active:bg-ghost-active disabled:text-ink-muted",
            // Primary: lime at rest, the brightest lime only under the cursor
            accent: `bg-brand px-[18px] text-brand-ink hover:bg-brand-hover active:bg-brand disabled:bg-brand-disabled disabled:text-ink-muted ${SVG_FILL}`,
            destructive: "bg-danger-fill text-white hover:bg-danger-fill-hover active:bg-danger-fill-hover disabled:opacity-50",
            errorText: `bg-transparent font-medium text-danger hover:bg-danger-bg disabled:text-text-error-disabled ${SVG_FILL}`,
            link: "h-auto px-0 font-medium text-link underline-offset-4 hover:underline",
        },
        shape: {
            default: "",
            /** Compact pill, kept for existing callers */
            rounded: "h-[30px] px-3 rounded-pill gap-2",
        },
    },
    defaultVariants: {
        size: "md",
        variant: "button-base-alt",
        shape: "default",
    },
});
const Button = React.forwardRef((_a, ref) => {
    var { className, variant, size, shape, asChild = false, icon, iconFill, children } = _a, props = __rest(_a, ["className", "variant", "size", "shape", "asChild", "icon", "iconFill", "children"]);
    const Comp = asChild ? Slot : "button";
    return (_jsx(Comp, Object.assign({ className: cn(buttonVariants({ variant, size, shape }), className), ref: ref }, props, { children: asChild ? (children) : (_jsxs(_Fragment, { children: [icon ? _jsx(Icon, { name: icon, fill: iconFill }) : null, children] })) })));
});
Button.displayName = "Button";
export { Button, buttonVariants };
